import { UnknownElementError } from '@/features/legends/editor/legend-document'
import { BinaryOperationEditorViewModel } from '@/features/legends/filters/types/BinaryOperationEditorViewModel'
import { ConjunctionEditorViewModel } from '@/features/legends/filters/types/ConjunctionEditorViewModel'
import { ConstantEditorViewModel } from '@/features/legends/filters/types/ConstantEditorViewModel'
import { ExpressionEditorViewModel } from '@/features/legends/filters/types/ExpressionEditorViewModel'
import { HasSkillsEditorViewModel } from '@/features/legends/filters/types/HasSkillsEditorViewModel'
import { IfEditorViewModel } from '@/features/legends/filters/types/IfEditorViewModel'
import type { FilterEditorViewModelOptions } from './FilterEditorViewModelBase'
import type { FilterType, Rule } from './rules'

/**
 * Any filter editor, narrowed by its `type`.
 */
export type AnyFilterEditor =
  | BinaryOperationEditorViewModel
  | ConjunctionEditorViewModel
  | ConstantEditorViewModel
  | ExpressionEditorViewModel
  | HasSkillsEditorViewModel
  | IfEditorViewModel

/**
 * Creates an empty filter editor of the type.
 *
 * Kept as functions rather than a map of classes: the filter types import
 * the builder, which imports this module, so the classes may not be defined
 * yet when this module loads.
 *
 * @param type
 * @param options
 */
export function createFilter(
  type: FilterType,
  options: FilterEditorViewModelOptions,
): AnyFilterEditor {
  switch (type) {
    case 'BinaryOperation':
      return new BinaryOperationEditorViewModel(options)
    case 'Conjunction':
      return new ConjunctionEditorViewModel(options)
    case 'Constant':
      return new ConstantEditorViewModel(options)
    case 'Expression':
      return new ExpressionEditorViewModel(options)
    case 'HasSkills':
      return new HasSkillsEditorViewModel(options)
    case 'If':
      return new IfEditorViewModel(options)
  }
}

/**
 * Creates a filter editor hydrated from the rule. Throws for a rule of a type
 * the builder doesn't know, which no editor can show.
 *
 * @param rule
 * @param options
 */
export function hydrateFilter(
  rule: Rule,
  options: FilterEditorViewModelOptions,
): AnyFilterEditor {
  if (rule.type === 'Unknown') {
    throw new UnknownElementError('filter', rule)
  }
  const editor = createFilter(rule.type, options)
  // SAFETY: the editor was created for the rule's own type.
  ;(editor as { hydrate(rule: Rule): void }).hydrate(rule)
  return editor
}
